import { ResponseTranslation } from '../Translations';
import { WithholdingResidency } from '../withholding/withholding';

/** Routing role of a provider bank account. Mirrors Finance.Models ProviderBankType. */
export enum ProviderBankType {
  Standard = 0,
  Intermediary = 1,
}

/**
 * Mirrors `Sierra.Finance.Models.Enums.BankAccountType` — declaration order is the wire format, so these
 * numbers must not be reordered.
 *
 * PAYROLL and FIXED_TERM are real for our own bank accounts, but a provider destination is only ever
 * offered CHECKING or SAVINGS (#5079): a payables transfer goes to an operating account. The restriction
 * lives in the picker, not in this enum, so the two halves of the wire format stay identical.
 */
export enum BankAccountType {
  CHECKING = 0,
  SAVINGS = 1,
  PAYROLL = 2,
  FIXED_TERM = 3,
}

/** One of a provider's payable bank accounts (destination). Mirrors Finance.Models ProviderBankAccount.
 * The AP payment request picks one of these by id and freezes it into its banking snapshot. */
export interface ProviderBankAccount {
  id?: string | null;
  alias?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  swiftBicCode?: string | null;
  ibanCode?: string | null;
  bankAddress?: string | null;
  currency?: number | null;
  /** Direct (Standard) destination vs Intermediary correspondent bank. An Intermediary is never offered
   *  as a payment destination — the money passes through it and lands in the beneficiary account, so it is
   *  the beneficiary that a payment is checked and executed against (#5143). */
  bankType?: ProviderBankType;
  /** What the bank calls the account — checking or savings (#5079). Not to be confused with `bankType`
   *  above, which is the routing role. */
  accountType?: BankAccountType;
  isPrimary?: boolean;
}

export interface ProviderDetails {
  providerTypeCode?: string;
  purchaseOrganizationCode?: string;
  paymentTermsCode?: string;
  currencyCode?: string;
  providerBlockReasonCode?: string;
  accountGroupCode?: string;
  sgiCode?: string;
  cisCode?: string;
  leadAssociateUserName?: string;
  lastPurchaseOrderDate?: Date;
  // Accounts Payable fields — mirrors Finance.Models ProviderDetailsDto. Drive AP document autofill.
  // Tax id is NOT here — it lives on Business.TaxIndicatorCode (see [[business-tax-id-field]]).
  apGlAccountReference?: string;
  apGlAccountNumber?: string;
  /** #4785 — denormalized AP control account name; AP screens lead with this and show the number beneath.
   *  Localized and API-derived from the account reference — read-only, never sent by the UI. */
  apGlAccountName?: ResponseTranslation;
  creditDays?: number;
  creditLimit?: number;
  isOneTimeVendor?: boolean;
  /** Provider commercial / trade name (distinct from the legal name on Business.name). */
  commercialName?: string;
  /** Payment method master-data code (Transfer / Check / Cash …). */
  paymentMethodCode?: string;
  /** Tax status / regime master-data code (e.g. Zona Libre). */
  taxRegimeCode?: string;
  /** ISO-3 country where the provider is domiciled (#4304) — withholding jurisdiction (read-only on AP doc). */
  /** Provider withholding residency (#4304) — Resident / Non-resident / Any; read-only on the AP doc. */
  withholdingResidency?: WithholdingResidency;
  /** The provider's usual withholding code (WithholdingTaxCode.code, #4304) — AP default. */
  defaultWithholdingCode?: string;
  /** Payable bank accounts (destinations). Selected by the AP payment request. */
  banks?: ProviderBankAccount[];
  /** Certificate attachments (S3 keys). */
  certificateAttachments?: string[];
}
